import {
  Children,
  cloneElement,
  ComponentProps,
  createContext,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { ChevronUpIcon, ChevronDownIcon, ChevronSelectorVerticalIcon } from '@/assets/icons';
import { mergeClasses } from '@/utils';
import { Checkbox, Badge, SuspenseImage } from '@/components/common';

import {
  SortDirection,
  ITableContextValue,
  TableProps,
  ITableRow,
  TableCellProps,
} from './Table.interfaces';
import {
  DEFAULT_STATUS_VARIANT_ICON,
  TABLE_CELL_ICON_VARIANTS,
  TABLE_HEADER_VARIANTS,
  TABLE_ROW_VARIANTS,
} from './Table.constants';

const TableContext = createContext<ITableContextValue>({});

const Table = ({
  className,
  size = 'default',
  hasCheckBox = false,
  selectedRows: controlledSelectedRows,
  onSelectedRowsChange,
  rowIds = [],
  ...props
}: TableProps) => {
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string | number>>(new Set());

  const selectedRows = controlledSelectedRows ?? internalSelectedRows;
  const setSelectedRows = onSelectedRowsChange ?? setInternalSelectedRows;

  const isAllSelected = rowIds.length > 0 && selectedRows.size === rowIds.length;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < rowIds.length;

  const onSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(rowIds));
    }
  }, [isAllSelected, rowIds, setSelectedRows]);

  const onSelectRow = useCallback(
    (rowId: string | number) => {
      const newSelectedRows = new Set(selectedRows);
      if (newSelectedRows.has(rowId)) {
        newSelectedRows.delete(rowId);
      } else {
        newSelectedRows.add(rowId);
      }
      setSelectedRows(newSelectedRows);
    },
    [selectedRows, setSelectedRows],
  );

  const contextValue: ITableContextValue = {
    size,
    hasCheckBox,
    checkboxState: {
      selectedRows,
      isAllSelected,
      isIndeterminate,
    },
    onSelectAll: onSelectAll,
    onSelectRow: onSelectRow,
    rowIds,
  };

  return (
    <TableContext.Provider value={contextValue}>
      <div
        data-slot="table-container"
        className={mergeClasses(
          'relative w-full overflow-x-auto [scrollbar-width:thin] border border-neutral-n4 rounded-softer',
          className,
        )}
      >
        <table
          data-slot="table"
          className="w-full caption-bottom border-separate border-spacing-0 [&_tr:last-of-type_td]:border-none"
          {...props}
        />
      </div>
    </TableContext.Provider>
  );
};

const TableHeader = ({ className, children, ...props }: ComponentProps<'thead'>) => {
  const { size, hasCheckBox, checkboxState, onSelectAll } = useContext(TableContext);

  return (
    <thead
      data-slot="table-header"
      className={mergeClasses('', TABLE_HEADER_VARIANTS({ size: size }), className)}
      {...props}
    >
      {Children.map(children, (child) => {
        if (isValidElement(child) && hasCheckBox) {
          return cloneElement(child as ReactElement<ComponentProps<'tr'>>, {
            children: (
              <>
                <TableHead className="w-[72px] [&>*]:align-middle">
                  <Checkbox
                    checked={
                      checkboxState?.isIndeterminate
                        ? 'indeterminate'
                        : checkboxState?.isAllSelected
                    }
                    onClick={onSelectAll}
                  />
                </TableHead>
                {(child as ReactElement<{ children?: ReactNode }>).props.children}
              </>
            ),
          });
        }
        return child;
      })}
    </thead>
  );
};

const TableBody = ({ className, ...props }: ComponentProps<'tbody'>) => {
  return (
    <tbody
      data-slot="table-body"
      className={mergeClasses('[&_tr:last-child]:border-0 [&_tr:hover]:bg-neutral-n1', className)}
      {...props}
    />
  );
};

const TableFooter = ({ className, children, ...props }: ComponentProps<'tfoot'>) => {
  const { hasCheckBox } = useContext(TableContext);

  return (
    <tfoot
      data-slot="table-footer"
      className={mergeClasses('bg-muted/50 border-t font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    >
      {Children.map(children, (child) => {
        if (isValidElement(child) && hasCheckBox) {
          return cloneElement(child as ReactElement<ComponentProps<'tr'>>, {
            children: (
              <>
                <TableCell />
                {(child as ReactElement<{ children?: ReactNode }>).props.children}
              </>
            ),
          });
        }
        return child;
      })}
    </tfoot>
  );
};

const TableRow = ({ className, rowId, children, ...props }: ITableRow) => {
  const { size, hasCheckBox, checkboxState, onSelectRow } = useContext(TableContext);
  const isSelected = rowId !== undefined && checkboxState?.selectedRows.has(rowId);

  return (
    <tr
      data-slot="table-row"
      data-state={isSelected ? 'selected' : undefined}
      className={mergeClasses(
        'data-[state=selected]:bg-muted transition-colors',
        TABLE_ROW_VARIANTS({ size }),
        className,
      )}
      {...props}
    >
      {hasCheckBox && rowId !== undefined && (
        <TableCell className="text-center [&>div]:align-middle">
          <Checkbox checked={isSelected} onClick={() => onSelectRow?.(rowId)} />
        </TableCell>
      )}
      {children}
    </tr>
  );
};

const TableHead = ({
  className,
  sortable = false,
  sortDirection: controlledSortDirection,
  onSort,
  children,
  ...props
}: ComponentProps<'th'> & {
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: (direction: SortDirection) => void;
}) => {
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(null);

  // Determine if component is controlled
  const isControlled = controlledSortDirection !== undefined;

  // Use controlled sort direction if provided, otherwise use internal state
  const sortDirection = isControlled ? controlledSortDirection : internalSortDirection;

  const onClick = () => {
    if (!sortable) return;

    let newDirection: SortDirection;
    if (sortDirection === null) {
      newDirection = 'asc';
    } else if (sortDirection === 'asc') {
      newDirection = 'desc';
    } else {
      newDirection = null;
    }

    // Only update internal state if not controlled
    if (!isControlled) {
      setInternalSortDirection(newDirection);
    }
    onSort?.(newDirection);
  };

  const content = (
    <div className="inline-flex items-center font-semibold text-body-sm text-neutral-n8">
      {children}
      {sortable && (
        <span className="ml-2 inline-flex text-brand-primary-darkest">
          {sortDirection === null || sortDirection === undefined ? (
            <ChevronSelectorVerticalIcon className="size-base" />
          ) : sortDirection === 'asc' ? (
            <ChevronUpIcon className="size-base" />
          ) : (
            <ChevronDownIcon className="size-base" />
          )}
        </span>
      )}
    </div>
  );

  return (
    <th
      data-slot="table-head"
      className={mergeClasses(
        'text-foreground h-10 text-left align-middle font-medium whitespace-nowrap px-m border-b border-b-neutral-n4',
        sortable && 'cursor-pointer select-none hover:bg-neutral-n2',
        className,
      )}
      onClick={onClick}
      {...props}
    >
      {content}
    </th>
  );
};

const TableCell = (props: TableCellProps) => {
  const { className, children, ...rest } = props;
  const { size } = useContext(TableContext);
  const cellType = 'cellType' in props ? props.cellType : 'text';

  const baseClasses = mergeClasses(
    'align-middle whitespace-nowrap border-b border-b-neutral-n4 px-m text-body-sm font-medium color-brand-primary-darkest',
    className,
  );

  const renderContent = () => {
    switch (cellType) {
      case 'text-avatar': {
        const { avatarSrc, avatarAlt, avatarFallback } = props as Extract<
          TableCellProps,
          { cellType: 'text-avatar' }
        >;
        return (
          <div className="flex items-center gap-s">
            <div className="size-8 shrink-0 overflow-hidden rounded-full bg-neutral-n3">
              {avatarSrc ? (
                <SuspenseImage
                  src={avatarSrc}
                  alt={avatarAlt || ''}
                  className="size-l object-cover"
                />
              ) : (
                <span className="flex size-l items-center justify-center text-xs font-medium text-neutral-n7">
                  {avatarFallback || '?'}
                </span>
              )}
            </div>
            <span>{children}</span>
          </div>
        );
      }

      case 'text-icon': {
        const { icon, iconPosition = 'left' } = props as Extract<
          TableCellProps,
          { cellType: 'text-icon' }
        >;
        return (
          <div className="inline-flex items-center gap-s align-middle">
            {iconPosition === 'left' && (
              <span
                className={mergeClasses(
                  TABLE_CELL_ICON_VARIANTS({ size }),
                  'shrink-0 text-brand-primary-darkest',
                )}
              >
                {icon}
              </span>
            )}
            <span>{children}</span>
            {iconPosition === 'right' && (
              <span
                className={mergeClasses(
                  TABLE_CELL_ICON_VARIANTS({ size }),
                  'shrink-0 text-brand-primary-darkest',
                )}
              >
                {icon}
              </span>
            )}
          </div>
        );
      }

      case 'tag': {
        const { tagVariant = 'default' } = props as Extract<TableCellProps, { cellType: 'tag' }>;
        return (
          <Badge className="items-center border-none !px-4" variant={tagVariant}>
            {children}
          </Badge>
        );
      }

      case 'icon': {
        const { icon } = props as Extract<TableCellProps, { cellType: 'icon' }>;
        return (
          <span
            className={mergeClasses(
              TABLE_CELL_ICON_VARIANTS({ size }),
              'inline-flex text-brand-primary-darkest',
            )}
          >
            {icon}
          </span>
        );
      }

      case 'status': {
        const { statusVariant = 'default', statusIcon } = props as Extract<
          TableCellProps,
          { cellType: 'status' }
        >;
        return (
          <Badge className="items-center gap-s" variant={statusVariant}>
            <span
              className={mergeClasses(
                'rounded-full',
                size === 'sm' ? '[&_*]:size-s-plus' : '[&_*]:size-base',
              )}
            >
              {statusIcon || DEFAULT_STATUS_VARIANT_ICON[statusVariant]}
            </span>
            {children}
          </Badge>
        );
      }

      case 'text-dot': {
        const { dotColors, dotPosition = 'left' } = props as Extract<
          TableCellProps,
          { cellType: 'text-dot' }
        >;
        return (
          <div className="inline-flex items-center gap-s">
            {dotPosition === 'left' && (
              <span
                className={mergeClasses(
                  'size-2 shrink-0 rounded-full bg-brand-primary-base',
                  dotColors,
                )}
              />
            )}
            <span>{children}</span>
            {dotPosition === 'right' && (
              <span
                className={mergeClasses(
                  'size-2 shrink-0 rounded-full bg-brand-primary-base',
                  dotColors,
                )}
              />
            )}
          </div>
        );
      }

      case 'skeleton': {
        const { skeletonWidth = '100%' } = props as Extract<
          TableCellProps,
          { cellType: 'skeleton' }
        >;
        return (
          <div
            className="h-base-plus animate-pulse rounded bg-neutral-n3"
            style={{ width: skeletonWidth }}
          />
        );
      }

      case 'custom':
      case 'text':
      default:
        return children;
    }
  };

  return (
    <td data-slot="table-cell" className={baseClasses} {...rest}>
      {renderContent()}
    </td>
  );
};

const TableCaption = ({ className, ...props }: ComponentProps<'caption'>) => {
  return (
    <caption
      data-slot="table-caption"
      className={mergeClasses('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  );
};

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  type SortDirection,
  type TableCellProps,
};
